import { useMemo, useState, type ReactNode } from 'react';
import {
  Wallet, Receipt, FileMinus, CalendarDays, Download, Landmark, ArrowLeftRight, Printer, Percent, Building2,
  ShieldCheck, ListOrdered,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, StatCard } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/cn';
import { toCsv, downloadCsv } from '@/lib/csv';
import { getActiveBranchId } from '@/lib/api/session';
import { printHtml } from '@/lib/print';
import { useAuth } from '@/hooks/useAuth';
import { useBranding } from '@/hooks/useBranding';
import { toast } from '@/hooks/useToast';
import { useDayBook } from './useDayBook';
import { dayBookPrintHtml } from './dayBookPrint';
import type { DayBookBorrowSection, DayBookDepartment, DayBookMoney, DayBookReport } from './types';

const iso = (d: Date) => d.toISOString().slice(0, 10);
const daysAgo = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };

const INR = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const INR_WHOLE = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
const money = (v: string | number | null | undefined) => INR.format(parseFloat(String(v ?? 0)) || 0);
const whole = (v: number) => INR_WHOLE.format(v || 0);
const shortDate = (v: string) => new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });

const PRESETS: Array<{ id: string; label: string; from: () => string; to: () => string }> = [
  { id: 'today', label: 'Today', from: () => iso(new Date()), to: () => iso(new Date()) },
  { id: '7d', label: '7 days', from: () => daysAgo(6), to: () => iso(new Date()) },
  { id: '30d', label: '30 days', from: () => daysAgo(29), to: () => iso(new Date()) },
];

// Same keys as backend/utils/dayBookBilling.js's DEPARTMENTS.
const DEPARTMENT_OPTIONS: Array<{ key: DayBookDepartment; label: string }> = [
  { key: 'consultation', label: 'Consultation' },
  { key: 'registration', label: 'Registration' },
  { key: 'laboratory', label: 'Laboratory' },
  { key: 'pharmacy', label: 'Pharmacy' },
  { key: 'procedure', label: 'Procedures' },
  { key: 'room_nursing', label: 'Room & nursing' },
  { key: 'emergency', label: 'Emergency services' },
  { key: 'other', label: 'Other departments' },
];

type Billing = DayBookReport['billing'];

/**
 * The day-end close (ISS-09), extended 2026-10-29 with the billing side:
 * what was BILLED (gross, discounts, GST, net), COLLECTED, credited back and
 * still OUTSTANDING — split OP / IP / Emergency and by department
 * (consultation, registration, laboratory, pharmacy, procedures, room &
 * nursing, emergency services, other), plus insurance billing and claims.
 * Every split adds back up to the same totals: backend/utils/dayBookBilling.js
 * classifies each bill line once and shares bill-level money (payments,
 * credits, dues) by those lines.
 *
 * Collections stay gross AND net of what was credited back (credit notes,
 * pharmacy returns) — never silently netted. "Outstanding" is patient and
 * insurer dues at the end of the range, not a cash-drawer count (no shift
 * concept exists). Medicine Borrow & Return payments are in Collected and in
 * the by-mode split; what other hospitals owe is kept apart from patient dues.
 *
 * The department filter narrows the bill register and the headline tiles to
 * one department; the tables always show every department.
 */
export default function DayBookPanel() {
  const [from, setFrom] = useState(iso(new Date()));
  const [to, setTo] = useState(iso(new Date()));
  const [department, setDepartment] = useState<DayBookDepartment | ''>('');
  const [printing, setPrinting] = useState(false);
  const { user } = useAuth();
  const { branding } = useBranding();

  const query = useDayBook(from, to, department || null);
  const report = query.data;
  const billing = report?.billing;
  const deptRow = department && billing ? billing.by_department.find((d) => d.key === department) ?? null : null;
  const departmentLabel = DEPARTMENT_OPTIONS.find((d) => d.key === department)?.label ?? null;

  const activePreset = PRESETS.find((p) => p.from() === from && p.to() === to)?.id ?? null;

  const paymentTotal = useMemo(
    () => (report?.by_payment_mode ?? []).reduce((s, m) => s + (parseFloat(m.amount) || 0), 0),
    [report?.by_payment_mode]
  );

  const exportCsv = () => {
    if (!report) return;
    downloadCsv(`day-book_${from}_to_${to}${department ? `_${department}` : ''}.csv`, dayBookCsv(report, departmentLabel));
  };

  const print = async () => {
    if (!report || printing) return;
    setPrinting(true);
    try {
      const branchName = user?.branches?.find((b) => b.id === getActiveBranchId())?.name ?? null;
      const r = await printHtml(dayBookPrintHtml(report, {
        hospitalName: branding.hospital_name, branchName, departmentLabel, printedBy: user?.full_name ?? null,
      }));
      if (r.fallback) toast({ title: 'Opened in a new tab', description: 'This browser could not print directly — print from the new tab.' });
    } catch (err) {
      toast({ title: 'Could not print the day book', description: err instanceof Error ? err.message : String(err), variant: 'danger' });
    } finally {
      setPrinting(false);
    }
  };

  return (
    <section className="mt-6">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-bold">
            <Landmark size={17} className="text-teal" /> Day book
          </h2>
          <p className="mt-0.5 text-[12px] text-ink-muted">
            The day-end close — what was billed, collected and is still owed, by OP/IP and by department, with collections by mode.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div className="inline-flex rounded-lg border border-border p-0.5">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { setFrom(p.from()); setTo(p.to()); }}
                className={cn(
                  'rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors',
                  activePreset === p.id ? 'bg-teal text-white' : 'text-ink-muted hover:bg-muted'
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          <FormField label="From">
            <div className="relative">
              <CalendarDays size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
              <Input type="date" className="w-auto pl-8" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
            </div>
          </FormField>
          <FormField label="To">
            <div className="relative">
              <CalendarDays size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
              <Input type="date" className="w-auto pl-8" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
            </div>
          </FormField>
          <FormField label="Department">
            <NativeSelect className="w-auto" value={department} onChange={(e) => setDepartment(e.target.value as DayBookDepartment | '')}>
              <option value="">All departments</option>
              {DEPARTMENT_OPTIONS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </NativeSelect>
          </FormField>
          <Button variant="ghost" size="sm" disabled={!report} onClick={exportCsv}>
            <Download size={13} /> Export CSV
          </Button>
          <Button variant="ghost" size="sm" disabled={!report || printing} onClick={print}>
            <Printer size={13} /> {printing ? 'Printing…' : 'Print'}
          </Button>
        </div>
      </div>

      {query.isError ? (
        <ErrorState message={query.error instanceof ApiError ? query.error.message : 'Could not load the day book'} />
      ) : query.isLoading || !report || !billing ? (
        <LoadingState label="Loading the day book…" />
      ) : (
        <div className="space-y-4">
          <BillingTiles billing={billing} deptRow={deptRow} departmentLabel={departmentLabel} />

          <div className="grid items-start gap-4 min-[1100px]:grid-cols-2">
            <MoneyTable
              title={<><Building2 size={15} /> OP / IP billing</>}
              firstHeader="Setting"
              rows={billing.by_setting.map((r) => ({ key: r.key, label: r.label, money: r }))}
              total={{ label: 'All settings', money: billing.summary }}
            />
            <MoneyTable
              title={<><ListOrdered size={15} /> By department</>}
              firstHeader="Department"
              rows={billing.by_department.map((r) => ({ key: r.key, label: r.label, money: r }))}
              total={{ label: 'Total — all departments', money: billing.summary }}
              selectedKey={department || null}
              onSelect={(k) => setDepartment((cur) => (cur === k ? '' : (k as DayBookDepartment)))}
              note="A bill with lines in several departments is shared between them by its lines, so every rupee is counted once. Click a department to see only its bills."
            />
          </div>

          <InsuranceCard billing={billing} />

          <div className="grid items-start gap-4 min-[1100px]:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
            <Card>
              <CardHeader>
                <CardTitle><Wallet size={15} /> Daily billing &amp; collections</CardTitle>
                <span className="text-[11.5px] text-ink-muted">{shortDate(from)} – {shortDate(to)}</span>
              </CardHeader>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Billed</TableHead>
                      <TableHead className="text-right">Payments</TableHead>
                      <TableHead className="text-right">Collected</TableHead>
                      <TableHead className="text-right" title="Included in Collected">of which Medicine Borrow</TableHead>
                      <TableHead className="text-right">Credited/returned</TableHead>
                      <TableHead className="text-right">Net</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.daily.map((d) => (
                      <TableRow key={d.date}>
                        <TableCell className="text-[12px]">{shortDate(d.date)}</TableCell>
                        <TableCell className="text-right tabular-nums">{money(d.billed)}</TableCell>
                        <TableCell className="text-right tabular-nums">{d.payment_count}</TableCell>
                        <TableCell className="text-right tabular-nums">{money(d.collected)}</TableCell>
                        <TableCell className="text-right tabular-nums text-ink-muted">
                          {parseFloat(d.borrow_collected) > 0 ? money(d.borrow_collected) : '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-danger-ink">
                          {parseFloat(d.credited) + parseFloat(d.refunded) > 0
                            ? `− ${money(parseFloat(d.credited) + parseFloat(d.refunded))}`
                            : '—'}
                        </TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">{money(d.net_collected)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>

            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle><Receipt size={15} /> By payment mode</CardTitle>
                </CardHeader>
                {report.by_payment_mode.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-[13px] text-ink-muted">
                    Nothing collected in this period
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {report.by_payment_mode.map((m) => {
                      const amt = parseFloat(m.amount) || 0;
                      const share = paymentTotal > 0 ? (amt / paymentTotal) * 100 : 0;
                      return (
                        <div key={m.payment_mode}>
                          <div className="flex items-baseline justify-between gap-2 text-[12.5px]">
                            <span className="font-medium">{m.payment_mode}</span>
                            <span className="tabular-nums font-semibold">{money(m.amount)}</span>
                          </div>
                          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                            <div className="h-full rounded-full" style={{ width: `${share}%`, background: 'var(--teal)' }} />
                          </div>
                          <div className="mt-0.5 text-[10.5px] text-ink-muted">
                            {Math.round(share)}% · {m.payment_count} payment{m.payment_count === 1 ? '' : 's'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle><Landmark size={15} /> Collected by bill type</CardTitle>
                </CardHeader>
                {report.by_bill_type.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-[13px] text-ink-muted">
                    Nothing collected in this period
                  </div>
                ) : (
                  <div className="space-y-2">
                    {report.by_bill_type.map((b) => (
                      <div key={b.bill_type} className="flex items-center justify-between text-[12.5px]">
                        <Badge variant={b.bill_type === 'OPD' ? 'info' : b.bill_type === 'IP' ? 'warn' : b.bill_type === 'Medicine Borrow' ? 'neutral' : 'success'}>{b.bill_type}</Badge>
                        <span className="tabular-nums font-semibold">{money(b.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </div>

          <RegisterCard billing={billing} departmentLabel={departmentLabel} />

          <BorrowCard borrow={report.medicine_borrow} />

          {report.credit_notes.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle><FileMinus size={15} /> Credit notes issued</CardTitle>
                <Badge variant="neutral">{report.credit_notes.length}</Badge>
              </CardHeader>
              <div className="space-y-2">
                {report.credit_notes.map((cn) => (
                  <div key={cn.id} className="rounded-lg border border-border px-3 py-2">
                    <div className="flex items-baseline justify-between gap-2 text-[12px] font-semibold">
                      <span className="font-mono">{cn.credit_note_number}</span>
                      <span className="tabular-nums text-danger-ink">− {money(cn.total_amount)}</span>
                    </div>
                    <div className="mt-0.5 text-[11px] text-ink-muted">
                      {cn.patient_name} · against <span className="font-mono">{cn.bill_number}</span> ({cn.bill_type})
                      {cn.refund_status === 'Pending' && <> · <Badge variant="warn">Refund pending</Badge></>}
                    </div>
                    <div className="mt-0.5 text-[11px] text-ink-muted">{cn.reason}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </section>
  );
}

/** The four headline figures — for one department when the filter is set. */
function BillingTiles({ billing, deptRow, departmentLabel }: { billing: Billing; deptRow: DayBookMoney | null; departmentLabel: string | null }) {
  const s = billing.summary;
  const m: DayBookMoney = deptRow ?? s;
  const scope = deptRow && departmentLabel ? departmentLabel : null;
  const discountPct = m.gross > 0 ? Math.round((m.discount / m.gross) * 1000) / 10 : 0;
  return (
    <div className="grid gap-4 sm:grid-cols-2 min-[1100px]:grid-cols-4">
      <StatCard accentSide="left" accentColor="var(--teal)">
        <TileLabel icon={<Receipt size={12} />} text={`Total billed${scope ? ` · ${scope}` : ''}`} />
        <div className="mt-1.5 text-[26px] font-bold leading-none">{whole(m.net_billed)}</div>
        <div className="mt-1.5 text-[11px] text-ink-muted">
          {m.bill_count} bill{m.bill_count === 1 ? '' : 's'} · gross {whole(m.gross)}{m.gst > 0 ? ` · GST ${whole(m.gst)}` : ''}
        </div>
      </StatCard>
      <StatCard accentSide="left" accentColor="var(--color-warn-ink)">
        <TileLabel icon={<Percent size={12} />} text="Discounts" />
        <div className="mt-1.5 text-[26px] font-bold leading-none">{whole(m.discount)}</div>
        <div className="mt-1.5 text-[11px] text-ink-muted">
          {discountPct}% of gross{m.loyalty > 0 ? ` · + ${whole(m.loyalty)} paid in loyalty points` : ''}
        </div>
      </StatCard>
      <StatCard accentSide="left" accentColor="var(--color-success)">
        <TileLabel icon={<Wallet size={12} />} text="Collected" />
        <div className="mt-1.5 text-[26px] font-bold leading-none">{whole(m.collected)}</div>
        <div className="mt-1.5 text-[11px] text-ink-muted">
          {m.credited > 0 && <span className="text-danger-ink">{whole(m.credited)} credited/returned · </span>}
          net {whole(m.collected - m.credited)}{scope ? '' : ` · ${s.payments_count} payment${s.payments_count === 1 ? '' : 's'}`}
        </div>
      </StatCard>
      <StatCard accentSide="left" accentColor="var(--color-danger)">
        <TileLabel icon={<Landmark size={12} />} text="Outstanding dues" />
        <div className="mt-1.5 text-[26px] font-bold leading-none">{whole(m.outstanding)}</div>
        <div className="mt-1.5 text-[11px] text-ink-muted">
          {scope ? 'still owed on this department’s bills' : `patients ${whole(s.patient_outstanding)} · insurers ${whole(s.insurer_outstanding)}`}
        </div>
      </StatCard>
    </div>
  );
}

function TileLabel({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">
      {icon} {text}
    </div>
  );
}

function MoneyTable({ title, firstHeader, rows, total, selectedKey, onSelect, note }: {
  title: ReactNode;
  firstHeader: string;
  rows: Array<{ key: string; label: string; money: DayBookMoney }>;
  total: { label: string; money: DayBookMoney };
  selectedKey?: string | null;
  onSelect?: (key: string) => void;
  note?: string;
}) {
  const cells = (m: DayBookMoney) => (
    <>
      <TableCell className="text-right tabular-nums">{m.bill_count}</TableCell>
      <TableCell className="text-right tabular-nums">{money(m.net_billed)}</TableCell>
      <TableCell className="text-right tabular-nums text-ink-muted">{m.discount > 0 ? money(m.discount) : '—'}</TableCell>
      <TableCell className="text-right tabular-nums">{money(m.collected)}</TableCell>
      <TableCell className="text-right tabular-nums">{m.outstanding > 0 ? money(m.outstanding) : '—'}</TableCell>
    </>
  );
  return (
    <Card>
      <CardHeader><CardTitle>{title}</CardTitle></CardHeader>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{firstHeader}</TableHead>
              <TableHead className="text-right">Bills</TableHead>
              <TableHead className="text-right">Billed</TableHead>
              <TableHead className="text-right">Discount</TableHead>
              <TableHead className="text-right">Collected</TableHead>
              <TableHead className="text-right">Outstanding</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow
                key={r.key}
                className={cn(onSelect && 'cursor-pointer hover:bg-muted', selectedKey === r.key && 'bg-teal/10')}
                onClick={onSelect ? () => onSelect(r.key) : undefined}
                aria-selected={selectedKey === r.key || undefined}
              >
                <TableCell className="text-[12.5px] font-medium">{r.label}</TableCell>
                {cells(r.money)}
              </TableRow>
            ))}
            <TableRow className="font-semibold">
              <TableCell className="text-[12.5px]">{total.label}</TableCell>
              {cells(total.money)}
            </TableRow>
          </TableBody>
        </Table>
      </div>
      {note && <div className="mt-2 text-[11px] text-ink-muted">{note}</div>}
    </Card>
  );
}

function InsuranceCard({ billing }: { billing: Billing }) {
  const i = billing.insurance;
  const nothing = !i.billed_to_insurers && !i.collected_from_insurers && !i.insurer_outstanding && !i.claims_opened
    && !i.claims_submitted && !i.settlements_received && !i.written_off;
  return (
    <Card>
      <CardHeader>
        <CardTitle><ShieldCheck size={15} /> Insurance billing &amp; claims</CardTitle>
        <span className="text-[11.5px] text-ink-muted">Money from insurers is already inside Collected</span>
      </CardHeader>
      {nothing ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-[13px] text-ink-muted">
          No insurance billing or claim activity in this period
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 min-[1100px]:grid-cols-4">
          <BorrowFigure label="Billed to insurers" value={money(i.billed_to_insurers)}
            hint={`${i.bills_with_insurer_share} bill${i.bills_with_insurer_share === 1 ? '' : 's'} with an insurer share`} />
          <BorrowFigure label="Collected from insurers" value={money(i.collected_from_insurers)}
            hint={`TDS ${money(i.tds_withheld)} · written off ${money(i.written_off)}`} />
          <BorrowFigure label="Owed by insurers" value={money(i.insurer_outstanding)} hint="at the end of the period" tone="warn" />
          <BorrowFigure label="Claims submitted" value={`${i.claims_submitted}`}
            hint={`${money(i.claims_submitted_amount)} claimed · ${i.claims_opened} opened · ${i.settlements_received} settled (${money(i.settlements_net_received)})`} />
        </div>
      )}
      {billing.ip_deposits_received.count > 0 && (
        <div className="mt-3 text-[11.5px] text-ink-muted">
          IP advance deposits received in this period: {money(billing.ip_deposits_received.amount)} ({billing.ip_deposits_received.count}).
          They count in Collected when applied to the bill at discharge — never twice.
        </div>
      )}
    </Card>
  );
}

function RegisterCard({ billing, departmentLabel }: { billing: Billing; departmentLabel: string | null }) {
  const rows = billing.register;
  return (
    <Card>
      <CardHeader>
        <CardTitle><Receipt size={15} /> Bills raised{departmentLabel ? ` — ${departmentLabel}` : ''}</CardTitle>
        <Badge variant="neutral">{rows.length}{billing.register_truncated ? '+' : ''}</Badge>
      </CardHeader>
      {departmentLabel && <div className="mb-2 text-[11.5px] text-ink-muted">Amounts are this department&apos;s share of each bill.</div>}
      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-[13px] text-ink-muted">
          No bills raised in this period
        </div>
      ) : (
        <div className="max-h-[420px] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bill no.</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>OP/IP</TableHead>
                <TableHead className="text-right">Gross</TableHead>
                <TableHead className="text-right">Discount</TableHead>
                <TableHead className="text-right">Net</TableHead>
                <TableHead className="text-right">Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.bill_id}>
                  <TableCell className="font-mono text-[11.5px]">{r.bill_number}</TableCell>
                  <TableCell className="text-[12px]">{shortDate(r.created_at)}</TableCell>
                  <TableCell className="text-[12.5px]">
                    {r.patient_name}<span className="ml-1 text-[11px] text-ink-muted">{r.uhid}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={r.setting === 'IP' ? 'warn' : r.setting === 'ER' ? 'danger' : 'info'}>{r.setting}</Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{money(r.gross)}</TableCell>
                  <TableCell className="text-right tabular-nums text-ink-muted">{r.discount > 0 ? money(r.discount) : '—'}</TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">{money(r.net)}</TableCell>
                  <TableCell className={cn('text-right tabular-nums', r.balance > 0 && 'text-danger-ink')}>
                    {r.balance > 0 ? money(r.balance) : 'Paid'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {billing.register_truncated && (
        <div className="mt-2 text-[11px] text-ink-muted">Showing the first 1,000 bills — narrow the dates or export CSV.</div>
      )}
    </Card>
  );
}

/** One CSV with every section, each under its own heading line. */
function dayBookCsv(report: DayBookReport, departmentLabel: string | null): string {
  const b = report.billing;
  type MoneyRow = DayBookMoney & { label: string };
  const moneyCols: Array<[keyof MoneyRow, string]> = [
    ['label', 'Name'], ['bill_count', 'Bills'], ['gross', 'Gross'], ['discount', 'Discount'], ['gst', 'GST'],
    ['loyalty', 'Loyalty'], ['net_billed', 'Net billed'], ['collected', 'Collected'], ['credited', 'Credited'], ['outstanding', 'Outstanding'],
  ];
  const total: MoneyRow = { ...b.summary, label: 'Total' };
  return [
    `Day book,${report.from},${report.to}`,
    '', 'Summary', toCsv([total], moneyCols),
    '', 'OP / IP billing', toCsv<MoneyRow>([...b.by_setting, { ...total, label: 'All settings' }], moneyCols),
    '', 'By department', toCsv<MoneyRow>([...b.by_department, { ...total, label: 'Total - all departments' }], moneyCols),
    '', 'Insurance', toCsv([b.insurance], [
      ['billed_to_insurers', 'Billed to insurers'], ['collected_from_insurers', 'Collected from insurers'], ['tds_withheld', 'TDS withheld'],
      ['written_off', 'Written off'], ['insurer_outstanding', 'Owed by insurers'], ['claims_opened', 'Claims opened'],
      ['claims_submitted', 'Claims submitted'], ['claims_submitted_amount', 'Amount claimed'], ['settlements_received', 'Settlements'],
      ['settlements_net_received', 'Settlement net received'],
    ]),
    '', 'Daily', toCsv(report.daily, [
      ['date', 'Date'], ['billed', 'Billed'], ['discount', 'Discount'], ['payment_count', 'Payments'], ['collected', 'Collected'],
      ['borrow_collected', 'of which Medicine Borrow'], ['credited', 'Credit notes'], ['refunded', 'Pharmacy returns'], ['net_collected', 'Net'],
    ]),
    '', 'By payment mode', toCsv(report.by_payment_mode, [['payment_mode', 'Mode'], ['payment_count', 'Payments'], ['amount', 'Amount']]),
    '', `Bills raised${departmentLabel ? ` - ${departmentLabel}` : ''}`, toCsv(b.register, [
      ['bill_number', 'Bill no.'], ['created_at', 'Date'], ['patient_name', 'Patient'], ['uhid', 'UHID'], ['setting', 'OP/IP'],
      ['bill_type', 'Bill type'], ['gross', 'Gross'], ['discount', 'Discount'], ['gst', 'GST'], ['net', 'Net'], ['balance', 'Balance'],
    ]),
  ].join('\r\n');
}

/**
 * Medicine Borrow & Return for the range: what was lent, what came back, what
 * was paid (already inside Collected above), and what other hospitals still
 * owe at the end of the range — a receivable from hospitals, kept separate
 * from patient dues on purpose.
 */
function BorrowCard({ borrow }: { borrow: DayBookBorrowSection }) {
  const nothing = borrow.issued_count === 0 && borrow.returns_count === 0 && borrow.payment_count === 0
    && borrow.outstanding_amount === 0 && borrow.outstanding_units === 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle><ArrowLeftRight size={15} /> Medicine Borrow &amp; Return</CardTitle>
        <span className="text-[11.5px] text-ink-muted">Medicine lent to other hospitals</span>
      </CardHeader>
      {nothing ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-[13px] text-ink-muted">
          No medicine lent, returned or paid for in this period
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 min-[1100px]:grid-cols-4">
            <BorrowFigure label="Issued" value={`${borrow.issued_count} record${borrow.issued_count === 1 ? '' : 's'}`}
              hint={`${borrow.issued_units} units · ${money(borrow.issued_value)}`} />
            <BorrowFigure label="Items returned" value={`${borrow.returned_units} units`}
              hint={`${borrow.returns_count} return${borrow.returns_count === 1 ? '' : 's'} · back in stock`} />
            <BorrowFigure label="Payments collected" value={money(borrow.collected)}
              hint={`${borrow.payment_count} payment${borrow.payment_count === 1 ? '' : 's'} · included in Collected`} />
            <BorrowFigure label="Still owed by hospitals" value={money(borrow.outstanding_amount)}
              hint={`+ ${borrow.outstanding_units} units due back · not in patient dues`} tone="warn" />
          </div>
          {borrow.payments.length > 0 && (
            <div className="mt-3.5 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Paid on</TableHead>
                    <TableHead>Issue no.</TableHead>
                    <TableHead>Hospital</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {borrow.payments.map((pay) => (
                    <TableRow key={pay.id}>
                      <TableCell className="text-[12px]">{shortDate(pay.paid_on)}</TableCell>
                      <TableCell className="font-mono text-[11.5px]">{pay.issue_number}</TableCell>
                      <TableCell className="text-[12.5px]">{pay.hospital_name}</TableCell>
                      <TableCell className="text-[12px] text-ink-muted">{pay.item_name}</TableCell>
                      <TableCell className="text-[12px]">{pay.payment_mode}{pay.reference_number ? ` · ${pay.reference_number}` : ''}</TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">{money(pay.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function BorrowFigure({ label, value, hint, tone }: { label: string; value: string; hint: string; tone?: 'warn' }) {
  return (
    <div className={cn('rounded-lg bg-canvas px-3 py-2.5', tone === 'warn' && 'bg-warn-soft')}>
      <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">{label}</div>
      <div className="mt-1 text-[18px] font-bold leading-none tabular-nums">{value}</div>
      <div className="mt-1 text-[11px] text-ink-muted">{hint}</div>
    </div>
  );
}
